import _, {maxBy} from 'lodash';
import {useMemo} from 'react';
import {createSelector} from 'reselect';

import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {buildVariablesFromRecipientCustomFields} from '../../../helpers/customFieldHelpers';
import {buildTeammateNamespace} from '../../../helpers/modelHelpers';
import {selectDraftMessageUidsForConversation} from '../../../helpers/models/conversationHelpers';
import {isDraftSummaryVisuallyShared, isMessageVisuallyShared} from '../../../helpers/models/messageHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {AppParametricSelector, AppSelector} from '../../../helpers/reselectHelpers';
import {ActivityModel} from '../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../models/api/activitySourceModel';
import {CardModel} from '../../../models/api/cardModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {CustomFieldModel} from '../../../models/api/customFieldModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {DraftMessageModel, MessageModel, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {
  getAllIntegrationTypes,
  getIntegrationTypes,
} from '../../../store/data/integrationTypes/integrationTypesSelectors';
import {getDismissedIntegrationInformationBanners} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {AppState} from '../../appState';
import {ResourceMap} from '../../appTypes';
import {
  getHasAutoShareDrafts,
  getHasCannedAnswersResourceCustomFields,
} from '../../featureFlags/featureFlagsSelectors';
import {ComposerPanelState} from '../../panels/composer/composerPanelState';
import {getFlatPanels} from '../../panels/panelsSelectors';
import {PanelState} from '../../panels/panelsState';
import {ComposerPanelId, PanelTypesEnum, ViewerPanelId} from '../../panels/panelStateBase';
import {ViewerPanelPageTypesEnum, ViewerPanelState} from '../../panels/viewer/viewerPanelState';
import {getLocalize} from '../../stateless/internationalizationSelectors';
import {computeMessageActivitySourceVisualType} from '../activities/autoResponseMessageActivityHelpers';
import {getHasCompanyCardsEnabled} from '../cards/cardsSelectors';
import {getChannels} from '../channels/channelsSelectors';
import {
  makeGetRecipientCardForInterpolation,
  makeGetRecipientContactsFromCard,
  makeGetRecipientForInterpolation,
  makeGetVisualRecipientMap,
} from '../contacts/recipientsSelectors';
import {getOfflineDraftMessagesByConversationId} from '../conversations/conversationsSelectors';
import {
  getUserCustomAttributes,
  makeGetAccountCustomAttributesForCard,
  makeGetInterpolatedCustomAttributes,
} from '../customAttributes/customAttributesSelectors';
import {getCustomFieldsForInterpolation} from '../customFields/customFieldsSelectors';
import {getActivities, getConversations, getEnabledIntegrationsIds, getMessages} from '../dataSelectors';
import {getTimeFormat} from '../preferences/preferencesSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getUser, getUserId} from '../teammates/teammatesSelectors';
import {canReplyToMessage, findMessageById} from './messagesHelpers';

/*
 * Constants.
 */

const GOOGLE_DRIVE_REGEX = /(.*)@docs.google.com/;
const JIRA_REGEX = /jira@(.*)atlassian.net/;

/*
 * Derived property selectors for drafts.
 */

export type OptionalMessageSelector<P> = AppParametricSelector<P, MessageModel | undefined>;
type MessageSelector<P> = AppParametricSelector<P, MessageModel>;
type DraftSelector<P> = AppParametricSelector<P, DraftMessageModel>;
type MessageUidSelector<P> = AppParametricSelector<P, string>;

export function makeGetMessage<P>(getMessageUid: MessageUidSelector<P>) {
  return createSelector([getMessageUid, getMessages], (messageUid, messages) => {
    const message = messages[messageUid];
    if (!message) {
      throw new Error('A message could not be found for this uid.');
    }

    return message;
  });
}

export function makeGetMessageNamespace<P>(
  getMessage: AppParametricSelector<P, MessageModel | undefined>,
): AppParametricSelector<P, string> {
  // Higher-order selector that returns a selector based on the visual status.
  const getGetMessageNamespace = createSelector([getUserId, getMessage], (userId, message) => {
    // If the message could not be found, fall back to the user namespace.
    if (!message) {
      return () => buildTeammateNamespace(userId);
    }

    if (message.visualStatus === MessageVisualStatusesEnum.DRAFT) {
      return makeGetDraftMessageNamespace(() => message);
    }

    return makeGetSavedMessageNamespace(() => message);
  });

  return (state, props) => {
    // Evaluate the higher-order selector.
    const getMessageNamespace = getGetMessageNamespace(state, props);

    // Evaluate the regular selector.
    return getMessageNamespace(state, props);
  };
}

export function makeGetDraftMessageNamespace<P>(getDraft: DraftSelector<P>) {
  return createSelector([getUserId, getDraft, getChannels], (userId, draft, channels) => {
    const channel = channels[draft.fromChannelId];
    if (!channel) {
      reportToBugsnag(new Error('The channel for this draft could not be found.'));
      return buildTeammateNamespace(draft.authorId || userId);
    }

    return channel.namespace;
  });
}

export function makeGetSavedMessageNamespace<P>(getMessage: MessageSelector<P>) {
  return createSelector([getUserId, getMessage, getConversations], (userId, message, conversations) => {
    const conversation = conversations[message.conversationId];
    if (!conversation) {
      reportToBugsnag(new Error('The conversation for this draft could not be found.'));
      return buildTeammateNamespace(userId);
    }

    return conversation.namespace;
  });
}

function makeGetDraftConversationId<P>(getDraft: DraftSelector<P>) {
  return createSelector(getDraft, (draft) => draft.conversationId);
}

function makeGetDraftMessageId<P>(getDraft: DraftSelector<P>) {
  return createSelector(getDraft, (draft) => draft.id);
}

export type DraftInReplyToMessageSelector<P> = AppParametricSelector<P, MessageModel | undefined>;

export function makeGetDraftInReplyToMessage<P>(
  getDraft: DraftSelector<P>,
): DraftInReplyToMessageSelector<P> {
  return createSelector([getDraft, getMessages], (draft, messages): MessageModel | undefined =>
    draft.inReplyToId !== undefined ? findMessageById(draft.inReplyToId, messages) : undefined,
  );
}

export type CanReplyToMessageSelector<P> = AppParametricSelector<P, boolean>;

export function makeGetCanReplyToMessage<P>(getDraft: DraftSelector<P>): CanReplyToMessageSelector<P> {
  return createSelector([makeGetDraftInReplyToMessage(getDraft)], (inReplyToMessage): boolean =>
    inReplyToMessage ? canReplyToMessage(inReplyToMessage) : false,
  );
}

/*
 * Authors.
 */

export function makeGetAuthor<P>(getMessage: MessageSelector<P>) {
  return createSelector([getMessage, getFindTeammate], (message, findTeammate) => {
    if (!message || !message.authorId) {
      return undefined;
    }

    return findTeammate(message.authorId, 'messagesSelectors makeGetAuthor');
  });
}

export function makeGetIsUserAuthor<P>(getAuthor: AppParametricSelector<P, TeammateModel | undefined>) {
  return createSelector([getUserId, getAuthor], (userId, author) => Boolean(author && userId === author.id));
}

/*
 * Variable Interpolation.
 */

/**
 * Returns a selector that generates a map of "customFieldName"->"customFieldValue" for the given recipient &
 * the current user.
 *
 * TODO: Add inbox & account custom fields support.
 */
export function makeGetCustomAttributesForInterpolation<P>(
  getRecipientForInterpolation: AppParametricSelector<P, VisualRecipientModel | undefined>,
  getDraftNamespace: AppParametricSelector<P, string>,
  _getHasCompanyCardsEnabled: AppSelector<boolean>,
  getCardsOverride?: AppSelector<ResourceMap<CardModel>>, // For testing.
  getCustomFieldsOverride?: AppSelector<ResourceMap<CustomFieldModel>>, // For testing.
) {
  const getRecipientCardForInterpolation = makeGetRecipientCardForInterpolation(
    getRecipientForInterpolation,
    getDraftNamespace,
    _getHasCompanyCardsEnabled,
    getCardsOverride,
  );
  const getAccountCustomAttributes = makeGetAccountCustomAttributesForCard(getRecipientCardForInterpolation);

  const getInterpolatedUserCustomAttributes = makeGetInterpolatedCustomAttributes(getUserCustomAttributes);
  const getInterpolatedAccountCustomAttributes =
    makeGetInterpolatedCustomAttributes(getAccountCustomAttributes);

  return createSelector(
    [
      getHasCannedAnswersResourceCustomFields,
      getRecipientCardForInterpolation,
      getCustomFieldsOverride || getCustomFieldsForInterpolation,
      getInterpolatedUserCustomAttributes,
      getInterpolatedAccountCustomAttributes,
      getLocalize,
      getTimeFormat,
    ],
    (
      hasCannedAnswersResourceCustomFields,
      recipientCard,
      customFields,
      userInterpolatedCustomAttributes,
      accountInterpolatedCustomAttributes,
      localizer,
      timeFormat,
    ) => {
      const recipientInterpolation = buildVariablesFromRecipientCustomFields(
        recipientCard,
        customFields,
        localizer,
        timeFormat,
      );

      if (!hasCannedAnswersResourceCustomFields) {
        return recipientInterpolation;
      }

      return {
        ...userInterpolatedCustomAttributes,
        ...accountInterpolatedCustomAttributes,
        ...recipientInterpolation,
      };
    },
  );
}

export function makeGetVariableInterpolator<P>(getDraft: DraftSelector<P>) {
  const getDraftNamespace = makeGetDraftMessageNamespace(getDraft);
  const getVisualRecipientMap = makeGetVisualRecipientMap(getDraftNamespace, getDraft);

  // Inputs to VariableInterpolator
  const getDraftConversationId = makeGetDraftConversationId(getDraft);
  const getDraftMessageId = makeGetDraftMessageId(getDraft);
  const getRecipientForInterpolation = makeGetRecipientForInterpolation(getVisualRecipientMap);
  const getRecipientContactsFromCard = makeGetRecipientContactsFromCard(getRecipientForInterpolation);
  const getCustomAttributesForInterpolation = makeGetCustomAttributesForInterpolation(
    getRecipientForInterpolation,
    getDraftNamespace,
    getHasCompanyCardsEnabled,
  );

  return createSelector(
    [
      getUser,
      getDraftConversationId,
      getDraftMessageId,
      getRecipientForInterpolation,
      getRecipientContactsFromCard,
      getCustomAttributesForInterpolation,
    ],
    (user, conversationId, messageId, recipient, recipientContacts, customAttributes) =>
      new VariableInterpolator(
        user,
        conversationId,
        messageId,
        recipient,
        recipientContacts,
        customAttributes,
      ),
  );
}

/*
 * Integration Types.
 */

// Creates a map keyed by integration notification emails, mapping to the integration type.
// Do NOT include external integrations.  This map helps determine when to display Integration Information Banners.
// External integrations cannot be enabled, so these banners would be very noisy and are difficult to supress.
const getIntegrationNotificationEmailMap = createSelector(getAllIntegrationTypes, (integrationTypes) => {
  // Build an object where the keys are the email_notifications.
  const integrationNotificationEmailMap = _(integrationTypes)
    .filter((i) => !i.external)
    .flatMap((i) => i.notificationEmails.map((e) => [e, i.id]))
    .fromPairs()
    .value();

  return integrationNotificationEmailMap;
});

export function makeGetRelatedIntegration<P>(getMessage: MessageSelector<P>) {
  return createSelector(
    [
      getMessage,
      getIntegrationTypes,
      getEnabledIntegrationsIds,
      getDismissedIntegrationInformationBanners,
      getIntegrationNotificationEmailMap,
    ],
    (
      message,
      integrationTypes,
      enabledIntegrationIds,
      dismissedIntegrationInformationBanners,
      integrationNotificationEmailMap,
    ) => {
      // Given a handle, deterine whether there is a related integration.
      const findIntegrationForThisHandle = (handle: string | undefined) => {
        if (!handle) {
          return undefined;
        }

        // Checks if the handle is a custom jira handle.
        if (JIRA_REGEX.test(handle)) {
          return 'jira';
        }

        if (GOOGLE_DRIVE_REGEX.test(handle)) {
          return 'googledrive';
        }

        return integrationNotificationEmailMap[handle];
      };

      // Find the first recipient handle that is an Integration Notification Email from Airtable.
      const suggestedIntegrationType = _(message.recipients)
        .map((r) => findIntegrationForThisHandle(r.handle))
        .compact()
        .first();

      // If none could be found, return early.
      if (!suggestedIntegrationType) {
        return undefined;
      }

      // If the integration is already enabled, no need to render the banner, early return.
      if (enabledIntegrationIds.includes(suggestedIntegrationType)) {
        return undefined;
      }

      // If the banner has already been dismissed, early return as well.
      if (dismissedIntegrationInformationBanners.includes(suggestedIntegrationType)) {
        return undefined;
      }

      // Return the integration type to be rendered into the banner.
      return integrationTypes[suggestedIntegrationType];
    },
  );
}

/*
 * Panel-related selectors for drafts.
 */

type DraftMessagePanelId = ComposerPanelId | ViewerPanelId;
type DraftMessagePanelIdSelector<P> = AppParametricSelector<P, DraftMessagePanelId>;

export function makeGetIsPanelDraftOwner<P>(getPanelId: DraftMessagePanelIdSelector<P>) {
  return createSelector(
    [getDraftMessagePanelIds, getPanelId],
    (draftMessagePanelIds, panelId) => (draftUid: string) => draftMessagePanelIds[draftUid] === panelId,
  );
}

const getDraftMessagePanelIds = createSelector(
  [getFlatPanels, getConversations, getOfflineDraftMessagesByConversationId],
  (panels, conversations, offlineDraftMessagesByConversationId) =>
    _(panels)
      .flatMap((panel) => mapPanelToDraft(conversations, offlineDraftMessagesByConversationId, panel))
      .groupBy((panel) => panel.draftUid)
      .mapValues((draftPanels) => {
        const topSequencePanel = maxBy(draftPanels, (panel) => panel.sequence);
        return topSequencePanel?.panelId;
      })
      .value(),
);

interface DraftMessagePanel<T extends DraftMessagePanelId> {
  panelId: T;
  draftUid: string;
  sequence: number;
}

function mapPanelToDraft(
  conversations: ResourceMap<ConversationModel>,
  offlineDraftMessagesByConversationId: Record<number, ReadonlyArray<MessageModel>>,
  panel: PanelState,
): DraftMessagePanel<DraftMessagePanelId>[] {
  switch (panel.type) {
    case PanelTypesEnum.COMPOSER:
      return [mapComposerPanelToDraft(panel)];

    case PanelTypesEnum.VIEWER:
      return mapViewerPanelToDrafts(conversations, offlineDraftMessagesByConversationId, panel);

    default:
      return [];
  }
}

function mapComposerPanelToDraft(panel: ComposerPanelState): DraftMessagePanel<ComposerPanelId> {
  return {panelId: panel.id, draftUid: panel.messageUid, sequence: panel.sequence};
}

function mapViewerPanelToDrafts(
  conversations: ResourceMap<ConversationModel>,
  offlineDraftMessagesByConversationId: Record<number, ReadonlyArray<MessageModel>>,
  panel: ViewerPanelState,
): DraftMessagePanel<ViewerPanelId>[] {
  // Find the conversation in the target panel.
  const page = panel.pages.current;
  if (page?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
    return [];
  }

  const conversation = conversations[page.conversationId];
  if (!page || !conversation) {
    return [];
  }

  // Gather all the drafts UIDs.
  const draftUids = selectDraftMessageUidsForConversation(conversation, offlineDraftMessagesByConversationId);

  // For each, find the corresponding sequence number.
  return draftUids.map((uid) => {
    const sequence = page.draftMessageSequences[uid] || panel.sequence;
    return {panelId: panel.id, draftUid: uid, sequence};
  });
}

/** Get a channel for a draft message. */
export function makeGetDraftChannel<P>(getDraft: DraftSelector<P>) {
  return createSelector([getDraft, getChannels], (draft, channels) => {
    const channel = channels[draft.fromChannelId];
    if (!channel) {
      throw new Error('The channel for the draft could not be found.');
    }

    return channel;
  });
}

type OptionalActivityIdSelector<P> = AppParametricSelector<P, ActivityModel['id'] | undefined>;

export function makeGetMessageActivitySourceVisualType<P>(getActivityId: OptionalActivityIdSelector<P>) {
  return createSelector(
    [getActivityId, getActivities],
    (activityId, activities): ActivitySourceVisualTypesEnum | undefined => {
      if (!activityId) {
        return undefined;
      }

      const activity = activities[activityId];
      return activity ? computeMessageActivitySourceVisualType(activity) : undefined;
    },
  );
}

export function makeGetIsMessageVisuallyShared<P>(
  getMessage: AppParametricSelector<P, MessageModel | undefined>,
) {
  return createSelector(
    [getMessage, getConversations, getHasAutoShareDrafts],
    (draftMessage, conversations, hasAutoShareDrafts) =>
      Boolean(
        draftMessage &&
          isMessageVisuallyShared(
            draftMessage,
            conversations[draftMessage.conversationId],
            hasAutoShareDrafts,
          ),
      ),
  );
}

export function makeGetIsDraftSummaryVisuallyShared<P>(
  getDraft: AppParametricSelector<P, DraftSummaryModel>,
  getConversationId: AppParametricSelector<P, number | undefined>,
) {
  return createSelector(
    [getDraft, getConversations, getConversationId, getHasAutoShareDrafts],
    (draftMessage, conversations, conversationId, hasAutoShareDrafts) =>
      isDraftSummaryVisuallyShared(
        draftMessage,
        (conversationId && conversations[conversationId]) || undefined,
        hasAutoShareDrafts,
      ),
  );
}

export function useIsDraftSummaryVisuallyShared(
  draft: DraftSummaryModel,
  conversationId: number | undefined,
) {
  const getIsDraftSummaryVisuallySharedSelector = useMemo(
    () =>
      makeGetIsDraftSummaryVisuallyShared(
        () => draft,
        () => conversationId,
      ),
    [conversationId, draft],
  );
  const isDraftSummaryVisuallySharedRes = useAppSelector((state: AppState) =>
    getIsDraftSummaryVisuallySharedSelector(state, {}),
  );

  return isDraftSummaryVisuallySharedRes;
}

export function useIsMessageVisuallyShared(message: MessageModel | undefined) {
  const getIsMessageVisuallySharedSelector = useMemo(
    () => makeGetIsMessageVisuallyShared(() => message),
    [message],
  );
  const isMessageVisuallySharedRes = useAppSelector((state: AppState) =>
    getIsMessageVisuallySharedSelector(state, {}),
  );

  return isMessageVisuallySharedRes;
}
